import { ComponentColors, ThemeColor } from '@/enums'
import { useSnackbar } from '@/providers'
import { useCompany, useSignPoa } from '@/services'
import { safeTimestamp, timestampToDateString, useGoBack } from '@/utils'
import { Button } from '@surein/ui'
import { find, propEq } from 'ramda'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { ButtonLink, Loader, Typography } from '../../atoms'
import { DatePicker, SignatureCanvas, TrustPartners } from '../../molecules'
import { Column } from '../../styled'
import {
  ButtonContainer,
  Content,
  PageHeader,
  StyledInput,
  UserInfo,
} from './styles'

interface UserProfile {
  email: string
  fullname: string
  birthdate?: number
}

interface OutletContextType {
  user: UserProfile
}

interface SignPoaViewProps {
  onSkip?: () => void
  companyId: string
  onSuccess: () => void
  textColor?: ThemeColor
  showTrustPartners?: boolean
  cancelText?: string
}

interface MessagesType {
  signPoa_title: string
  signPoa_subtitle: string
  birthday: string
  name: string
  signNow: string
  later: string
  successPoa: string
  contactMD: string
  yourName: string
}

const useMessages = (): MessagesType => {
  const { t } = useTranslation()

  return {
    signPoa_title: t('profile.menu.signPOA'),
    signPoa_subtitle: t('signPoa_subtitle'),
    birthday: t('birthday'),
    name: t('name'),
    signNow: t('signNow'),
    later: t('later'),
    successPoa: t('snackBars.success.onPoaSigned'),
    contactMD: t('snackBars.success.contactMD'),
    yourName: t('your_name'),
  }
}

export const SignPoaView: React.FC<SignPoaViewProps> = ({
  onSkip,
  companyId,
  onSuccess,
  textColor = ThemeColor.b100,
  showTrustPartners = false,
  cancelText,
}) => {
  const messages = useMessages()
  const snackbar = useSnackbar()
  const signPoaMutation = useSignPoa(companyId)
  const { user: userProfile } = useOutletContext<OutletContextType>()
  const [birthdate, setBirthdate] = useState<number>(
    safeTimestamp(userProfile.birthdate || ''),
  )
  const [signature, setSignature] = useState<string>()
  const { data, isLoading } = useCompany(companyId)

  const handleCancel = useGoBack()

  if (isLoading) return <Loader />

  const disabled = !find(propEq(userProfile.email, 'email'), data?.users || [])
    ?.userTypes?.MD

  const handleSubmit = () => {
    if (disabled) {
      snackbar.error(messages.contactMD)
    } else {
      signPoaMutation.mutateAsync({ signature, birthdate }).then(() => {
        snackbar.success(messages.successPoa)
        onSuccess()
      })
    }
  }

  return (
    <>
      <Content>
        <PageHeader>
          <Typography color={textColor} variant="h4">
            {messages.signPoa_title}
          </Typography>
          <Typography color={ThemeColor.b50} variant="p1Body">
            {messages.signPoa_subtitle}
          </Typography>
        </PageHeader>
        <SignatureCanvas
          onChange={(dataUrl?: string) => setSignature(dataUrl)}
          value={signature}
        />
        <UserInfo>
          <Column gap="8px" width="100%">
            <Typography variant="captionR">{messages.name}</Typography>
            <StyledInput
              defaultValue={userProfile.fullname}
              disabled
              placeholder={messages.yourName}
            />
          </Column>
          <Column gap="8px" width="100%">
            <Typography variant="captionR">{messages.birthday}</Typography>
            <DatePicker
              max={timestampToDateString(Date.now())}
              onChange={setBirthdate}
              value={birthdate}
            />
          </Column>
        </UserInfo>
        {showTrustPartners && <TrustPartners mode="dark" />}
        <ButtonContainer singleButton={!cancelText}>
          {cancelText && (
            <Button
              loading={signPoaMutation.isLoading}
              onClick={handleCancel}
              variant="muted"
              shape="square"
            >
              {cancelText}
            </Button>
          )}
          <Button
            disabled={!signature || typeof birthdate === 'undefined'}
            loading={signPoaMutation.isLoading}
            onClick={handleSubmit}
            variant="primary"
            shape="square"
          >
            {messages.signNow}
          </Button>
        </ButtonContainer>
      </Content>
      {onSkip && (
        <ButtonLink block color={ComponentColors.primary} onClick={onSkip}>
          {messages.later}
        </ButtonLink>
      )}
    </>
  )
}
